"use client";

import { CircleDollarSign, FileText, Search, Truck, Users, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ACTION_TYPES } from "@/core/tma";
import {
  buildCommandItems,
  filterCommandItems,
  invokeCommandItem,
  type CommandLoadInput,
} from "@/core/console/command-palette";

export function CommandPalette({ loads }: { loads: CommandLoadInput[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const items = useMemo(() => buildCommandItems({ loads, actions: ACTION_TYPES }), [loads]);
  const filtered = useMemo(() => filterCommandItems(items, query, 10), [items, query]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  function selectItem(item: (typeof filtered)[number]) {
    const invocation = invokeCommandItem(item);
    if (invocation.href) {
      window.location.assign(invocation.href);
      return;
    }
    setResult(`${invocation.message}: ${item.title}`);
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)} className="min-w-32 justify-start">
        <Search size={16} />
        Command
      </Button>
      {open ? (
        <div className="fixed inset-0 z-50 bg-foreground/20 px-3 py-16 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="mx-auto max-w-2xl overflow-hidden rounded-lg border border-border bg-card shadow-xl">
            <div className="flex items-center gap-3 border-b border-border px-4 py-3">
              <Search size={18} className="text-muted-foreground" />
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setResult(null);
                }}
                placeholder="Search loads, carriers, customers, actions"
                className="h-9 flex-1 border-0 bg-transparent text-sm outline-none"
              />
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => setOpen(false)}
                aria-label="Close command palette"
              >
                <X size={16} />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {filtered.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => selectItem(item)}
                  className="grid w-full grid-cols-[2rem_1fr_auto] items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-muted"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <CommandIcon kind={item.kind} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{item.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
                  </span>
                  {item.kind === "action" ? (
                    <Badge variant={item.requiresApproval ? "warn" : "good"}>
                      {item.requiresApproval ? "hold" : "stage"}
                    </Badge>
                  ) : (
                    <Badge variant="info">{item.kind}</Badge>
                  )}
                </button>
              ))}
              {filtered.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-muted-foreground">No command found</div>
              ) : null}
            </div>
            {result ? (
              <div className="border-t border-border bg-muted px-4 py-3 text-sm text-muted-foreground">{result}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

function CommandIcon({ kind }: { kind: string }) {
  if (kind === "load") return <FileText size={16} />;
  if (kind === "carrier") return <Truck size={16} />;
  if (kind === "customer") return <Users size={16} />;
  return <CircleDollarSign size={16} />;
}
